import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { Auth0Provider } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './store/store';
import { fetchOrganizationByToken, fetchOrganizationById, setOrganization } from './store/organizationSlice';
import { getOrgFromUrl, getOrgTokenFromPath } from './utils/urlUtils';
import { getStoredOrganization, resolveOrganizationId } from './utils/organizationSession';

// Note: localStorage.clear() removed to preserve organization persistence

// Reload if page is restored from the browser's back/forward cache (avoids blank resume in some WebViews)
window.addEventListener('pageshow', (e) => {
  const evt = e as PageTransitionEvent;
  if (evt.persisted) {
    window.location.reload();
  }
});

const container = document.getElementById('root');
const root = createRoot(container!);

const authorizationParams: any = {
  redirect_uri: window.location.origin,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  scope: 'openid profile email read:current_user update:current_user_metadata'
};

const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { current: organization, loading, error } = useSelector((state: RootState) => state.organization);
  const orgToken = getOrgTokenFromPath();
  const orgFromQuery = getOrgFromUrl();
  const storedOrganization = getStoredOrganization();

  useEffect(() => {
    // Step 1: Check URL for organization token (path)
    if (orgToken && !organization && !loading && !error) {
      // URL has organization token, fetch from API by token
      dispatch(fetchOrganizationByToken(orgToken));
    }
    // Step 1b: Check URL for organization_id (query parameter)
    else if (orgFromQuery && !organization && !loading && !error) {
      // URL has organization_id, fetch from API by ID
      dispatch(fetchOrganizationById(orgFromQuery));
    }
    // Step 2: No URL organization, restore from localStorage
    else if (!orgToken && !orgFromQuery && !organization && !loading && !error && storedOrganization) {
      dispatch(setOrganization(storedOrganization));
    }
  }, [dispatch, orgToken, orgFromQuery, organization, loading, error, storedOrganization]);

  const authorizationParams: any = {
    redirect_uri: window.location.origin,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: 'openid profile email read:current_user update:current_user_metadata',
  };

  // Step 3: Resolve organization_id for Auth0
  // Priority: URL query > Current Redux org > Stored org
  const orgId = resolveOrganizationId(orgToken, orgFromQuery, storedOrganization) || 
                (organization ? organization.organization_id : null);

  if (orgId) {
    authorizationParams.organization = orgId;
  }

  // Render a loading state while the organization is being fetched
  if (loading && orgToken) {
    return <div>Loading Organization...</div>; // Or a proper spinner component
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
      authorizationParams={authorizationParams}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <AuthWrapper>
          <App />
        </AuthWrapper>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);
